import React, { useState, useEffect, useRef } from 'react';
import { Palette, Upload, Trash2, Save, Loader2, Image as ImageIcon, Type } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { toast } from 'sonner';
import { storeApi, formatApiError } from '../lib/api';

const MAX_LOGO_KB = 500;

const LogoUploader = ({ title, description, value, onChange, onRemove, testId }) => {
  const inputRef = useRef(null);

  const handleFile = (file) => {
    if (!file) return;
    if (!file.type.startsWith('image/')) {
      toast.error('Carica un file immagine (PNG o JPEG)');
      return;
    }
    if (file.size > MAX_LOGO_KB * 1024) {
      toast.error(`Immagine troppo grande (max ${MAX_LOGO_KB} KB)`);
      return;
    }
    const reader = new FileReader();
    reader.onload = () => onChange(reader.result);
    reader.readAsDataURL(file);
  };

  return (
    <Card>
      <CardHeader className="pb-2">
        <CardTitle className="text-base">{title}</CardTitle>
        <CardDescription>{description}</CardDescription>
      </CardHeader>
      <CardContent className="space-y-3">
        <div className="h-24 border-2 border-dashed rounded-lg flex items-center justify-center bg-slate-50 overflow-hidden">
          {value ? (
            <img src={value} alt={title} className="max-h-full max-w-full object-contain p-2" />
          ) : (
            <div className="text-center text-muted-foreground">
              <ImageIcon className="h-6 w-6 mx-auto mb-1" />
              <p className="text-xs">Nessun logo caricato — verrà usato il default</p>
            </div>
          )}
        </div>
        <input
          ref={inputRef}
          type="file"
          accept="image/png,image/jpeg,image/webp"
          className="hidden"
          onChange={(e) => handleFile(e.target.files?.[0])}
          data-testid={`${testId}-input`}
        />
        <div className="flex gap-2">
          <Button variant="outline" size="sm" className="flex-1" onClick={() => inputRef.current?.click()} data-testid={`${testId}-upload-btn`}>
            <Upload className="h-4 w-4 mr-2" /> Carica
          </Button>
          {value && (
            <Button variant="ghost" size="sm" className="text-red-600" onClick={onRemove} data-testid={`${testId}-remove-btn`}>
              <Trash2 className="h-4 w-4" />
            </Button>
          )}
        </div>
      </CardContent>
    </Card>
  );
};

export default function StorePdfBrandingPage() {
  const [settings, setSettings] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [removed, setRemoved] = useState({ company: false, store: false });

  useEffect(() => {
    const load = async () => {
      try {
        const response = await storeApi.getPdfSettings();
        setSettings(response.data);
      } catch (error) {
        toast.error(formatApiError(error));
      } finally {
        setLoading(false);
      }
    };
    load();
  }, []);

  const set = (key) => (value) => setSettings((s) => ({ ...s, [key]: value }));

  const handleSave = async () => {
    setSaving(true);
    try {
      const payload = {
        font_family: settings.font_family,
        company_name: settings.company_name,
        store_brand_name: settings.store_brand_name,
        footer_text: settings.footer_text,
      };
      if (settings.company_logo?.startsWith('data:image/')) payload.company_logo = settings.company_logo;
      if (settings.store_logo?.startsWith('data:image/')) payload.store_logo = settings.store_logo;
      if (removed.company && !settings.company_logo) payload.remove_company_logo = true;
      if (removed.store && !settings.store_logo) payload.remove_store_logo = true;

      const response = await storeApi.updatePdfSettings(payload);
      setSettings(response.data);
      setRemoved({ company: false, store: false });
      toast.success('Branding PDF salvato: verrà applicato ai prossimi PDF generati');
    } catch (error) {
      toast.error(formatApiError(error));
    } finally {
      setSaving(false);
    }
  };

  if (loading || !settings) {
    return <div className="p-6"><div className="animate-pulse h-64 bg-gray-100 rounded-lg max-w-4xl mx-auto" /></div>;
  }

  return (
    <div className="p-6 max-w-4xl mx-auto" data-testid="pdf-branding-page">
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-3xl font-bold text-slate-900 flex items-center gap-2">
            <Palette className="h-7 w-7 text-blue-600" />
            Branding PDF Store
          </h1>
          <p className="text-muted-foreground mt-1">
            Loghi, font e testi usati nei PDF dei preventivi cliente
          </p>
        </div>
        <Button onClick={handleSave} disabled={saving} data-testid="save-branding-btn">
          {saving ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Save className="h-4 w-4 mr-2" />}
          Salva Branding
        </Button>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
        <LogoUploader
          title="Logo Aziendale (brand principale)"
          description="Mostrato in alto a sinistra in ogni pagina. Default: Plenitude"
          value={settings.company_logo}
          onChange={(v) => { set('company_logo')(v); setRemoved((r) => ({ ...r, company: false })); }}
          onRemove={() => { set('company_logo')(null); setRemoved((r) => ({ ...r, company: true })); }}
          testId="company-logo"
        />
        <LogoUploader
          title="Logo Store / Dealer"
          description="Mostrato in copertina a destra. Default: testo 'Main Solution'"
          value={settings.store_logo}
          onChange={(v) => { set('store_logo')(v); setRemoved((r) => ({ ...r, store: false })); }}
          onRemove={() => { set('store_logo')(null); setRemoved((r) => ({ ...r, store: true })); }}
          testId="store-logo"
        />
      </div>

      <Card className="mb-4">
        <CardHeader className="pb-2">
          <CardTitle className="text-base flex items-center gap-2"><Type className="h-4 w-4" /> Font e Nomi</CardTitle>
        </CardHeader>
        <CardContent className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <div>
            <Label className="text-xs text-muted-foreground">Font del documento</Label>
            <Select value={settings.font_family} onValueChange={set('font_family')}>
              <SelectTrigger data-testid="font-select"><SelectValue /></SelectTrigger>
              <SelectContent>
                {(settings.available_fonts || ['Montserrat', 'Poppins']).map((f) => (
                  <SelectItem key={f} value={f}>{f}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label className="text-xs text-muted-foreground">Nome azienda/brand</Label>
            <Input value={settings.company_name || ''} onChange={(e) => set('company_name')(e.target.value)} data-testid="company-name-input" />
          </div>
          <div>
            <Label className="text-xs text-muted-foreground">Nome store/dealer</Label>
            <Input value={settings.store_brand_name || ''} onChange={(e) => set('store_brand_name')(e.target.value)} data-testid="store-brand-input" />
          </div>
        </CardContent>
      </Card>

      <Card>
        <CardHeader className="pb-2">
          <CardTitle className="text-base">Testo commerciale finale</CardTitle>
          <CardDescription>Mostrato nell'ultima pagina del PDF, prima della firma</CardDescription>
        </CardHeader>
        <CardContent>
          <Textarea
            rows={4}
            value={settings.footer_text || ''}
            onChange={(e) => set('footer_text')(e.target.value)}
            data-testid="footer-text-input"
          />
        </CardContent>
      </Card>
    </div>
  );
}
